import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
  useState,
  useEffect,
} from "react";
import {
  getOfficeCalendarData,
  getCalendarPanelData,
  getAdvanceCalendarFilters,
  CalendarPanelDataParams,
  AdvanceCalendarFiltersParams,
} from "services/officeCalendarService";
import { getAppointmentTemplates } from "services/templateService";
import {
  ApiCalendarAppointment,
  ApiOffDay,
  DetailedAppointment,
} from "components/client/dashboard/calendar/calendarRedesign/types";
import { ClientDataContext } from "components/context/ClientDataContext";

interface PanelPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface AdvanceFilterPagination {
  page: number;
  total: number;
  totalPages: number;
}

interface OfficeCalendarState {
  calendarAppointments: ApiCalendarAppointment[];
  offDays: ApiOffDay[];
  loadingAppointments: boolean;
  panelAppointments: DetailedAppointment[];
  panelPagination: PanelPagination;
  loadingPanel: boolean;
  panelError: Error | null;
  advanceFilterAppointments: DetailedAppointment[];
  advanceFilterPagination: AdvanceFilterPagination;
  advanceFilterResultCount: number | null;
  loadingAdvanceFilters: boolean;
  advanceFilterError: Error | null;
}

type Action =
  | { type: "FETCH_START" }
  | {
      type: "FETCH_SUCCESS";
      payload: {
        calendarAppointments: ApiCalendarAppointment[];
        offDays: ApiOffDay[];
      };
    }
  | { type: "FETCH_ERROR" }
  | { type: "FETCH_PANEL_START" }
  | {
      type: "FETCH_PANEL_SUCCESS";
      payload: {
        panelAppointments: DetailedAppointment[];
        panelPagination: PanelPagination;
      };
    }
  | { type: "FETCH_PANEL_ERROR"; payload: Error }
  | { type: "FETCH_ADVANCE_FILTERS_START" }
  | {
      type: "FETCH_ADVANCE_FILTERS_SUCCESS";
      payload: {
        advanceFilterAppointments: DetailedAppointment[];
        advanceFilterPagination: AdvanceFilterPagination;
        advanceFilterResultCount: number;
      };
    }
  | { type: "FETCH_ADVANCE_FILTERS_ERROR"; payload: Error }
  | {
      type: "PATCH_CALENDAR_APPOINTMENT";
      payload: { id: number; changes: Partial<ApiCalendarAppointment> };
    }
  | { type: "RESET" };

const initialPagination: PanelPagination = {
  total: 0,
  page: 1,
  limit: 50,
  totalPages: 0,
};

const initialAdvanceFilterPagination: AdvanceFilterPagination = {
  page: 1,
  total: 0,
  totalPages: 1,
};

const initialState: OfficeCalendarState = {
  calendarAppointments: [],
  offDays: [],
  loadingAppointments: false,
  panelAppointments: [],
  panelPagination: initialPagination,
  loadingPanel: false,
  panelError: null,
  advanceFilterAppointments: [],
  advanceFilterPagination: initialAdvanceFilterPagination,
  advanceFilterResultCount: null,
  loadingAdvanceFilters: false,
  advanceFilterError: null,
};

function reducer(
  state: OfficeCalendarState,
  action: Action,
): OfficeCalendarState {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loadingAppointments: true };
    case "FETCH_SUCCESS":
      return {
        ...state,
        calendarAppointments: action.payload.calendarAppointments,
        offDays: action.payload.offDays,
        loadingAppointments: false,
      };
    case "FETCH_ERROR":
      return { ...state, loadingAppointments: false };
    case "PATCH_CALENDAR_APPOINTMENT":
      return {
        ...state,
        calendarAppointments: state.calendarAppointments.map((appt) =>
          appt.id === action.payload.id
            ? { ...appt, ...action.payload.changes }
            : appt,
        ),
      };
    case "FETCH_PANEL_START":
      return { ...state, loadingPanel: true, panelError: null };
    case "FETCH_PANEL_SUCCESS":
      return {
        ...state,
        panelAppointments: action.payload.panelAppointments,
        panelPagination: action.payload.panelPagination,
        loadingPanel: false,
        panelError: null,
      };
    case "FETCH_PANEL_ERROR":
      return { ...state, loadingPanel: false, panelError: action.payload };
    case "FETCH_ADVANCE_FILTERS_START":
      return {
        ...state,
        loadingAdvanceFilters: true,
        advanceFilterError: null,
        advanceFilterAppointments: [],
        advanceFilterResultCount: null,
      };
    case "FETCH_ADVANCE_FILTERS_SUCCESS":
      return {
        ...state,
        advanceFilterAppointments: action.payload.advanceFilterAppointments,
        advanceFilterPagination: action.payload.advanceFilterPagination,
        advanceFilterResultCount: action.payload.advanceFilterResultCount,
        loadingAdvanceFilters: false,
        advanceFilterError: null,
      };
    case "FETCH_ADVANCE_FILTERS_ERROR":
      return {
        ...state,
        loadingAdvanceFilters: false,
        advanceFilterError: action.payload,
      };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

interface OfficeCalendarContextValue extends OfficeCalendarState {
  patchCalendarAppointment: (
    id: number,
    changes: Partial<ApiCalendarAppointment>,
  ) => void;
  fetchCalendarData: (from: string, to: string) => Promise<void>;
  refetchCalendarData: () => Promise<void>;
  fetchPanelData: (params: CalendarPanelDataParams) => Promise<void>;
  refetchPanelData: () => Promise<void>;
  fetchAdvanceFilters: (params: AdvanceCalendarFiltersParams) => Promise<void>;
  refetchAdvanceFilters: () => Promise<void>;
  fetchTemplates: () => Promise<void>;
  viewMode: "calendar" | "list";
  setViewMode: (mode: "calendar" | "list") => void;
  isPanelOpen: boolean;
  setIsPanelOpen: (open: boolean) => void;
}

export const OfficeCalendarContext =
  createContext<OfficeCalendarContextValue | null>(null);

export const OfficeCalendarProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { data, dispatchAction } = useContext(ClientDataContext)!;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [viewMode, setViewMode] = useState<"calendar" | "list">("calendar");
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const lastFetchParams = useRef<{ from: string; to: string } | null>(null);
  const lastPanelParams = useRef<CalendarPanelDataParams | null>(null);
  const lastAdvanceFilterParams = useRef<AdvanceCalendarFiltersParams | null>(
    null,
  );

  const patchCalendarAppointment = useCallback(
    (id: number, changes: Partial<ApiCalendarAppointment>) => {
      dispatch({
        type: "PATCH_CALENDAR_APPOINTMENT",
        payload: { id, changes },
      });
    },
    [],
  );

  const fetchCalendarData = useCallback(async (from: string, to: string) => {
    if (!from || !to) return;

    lastFetchParams.current = { from, to };
    dispatch({ type: "FETCH_START" });
    try {
      const result = await getOfficeCalendarData(from, to);
      dispatch({
        type: "FETCH_SUCCESS",
        payload: {
          calendarAppointments: result?.appointments || [],
          offDays: result?.offDays || [],
        },
      });
    } catch (err) {
      console.error("Error fetching calendar data:", err);
      dispatch({ type: "FETCH_ERROR" });
    }
  }, []);

  const refetchCalendarData = useCallback(async () => {
    if (!lastFetchParams.current) return;
    const { from, to } = lastFetchParams.current;
    await fetchCalendarData(from, to);
  }, [fetchCalendarData]);

  const fetchPanelData = useCallback(
    async (params: CalendarPanelDataParams) => {
      if (!params?.apptIds?.length) {
        dispatch({
          type: "FETCH_PANEL_SUCCESS",
          payload: {
            panelAppointments: [],
            panelPagination: initialPagination,
          },
        });
        return;
      }

      lastPanelParams.current = params;
      dispatch({ type: "FETCH_PANEL_START" });
      try {
        const result = await getCalendarPanelData(params);
        if (result?.isSuccessful) {
          dispatch({
            type: "FETCH_PANEL_SUCCESS",
            payload: {
              panelAppointments: result.appointments || [],
              panelPagination: result.pagination || initialPagination,
            },
          });
        } else {
          throw new Error("Failed to fetch calendar panel data");
        }
      } catch (err) {
        console.error("Error fetching calendar panel data:", err);
        dispatch({
          type: "FETCH_PANEL_ERROR",
          payload:
            err instanceof Error
              ? err
              : new Error("Failed to fetch calendar panel data"),
        });
      }
    },
    [],
  );

  const refetchPanelData = useCallback(async () => {
    if (!lastPanelParams.current) return;
    await fetchPanelData(lastPanelParams.current);
  }, [fetchPanelData]);

  const fetchAdvanceFilters = useCallback(
    async (params: AdvanceCalendarFiltersParams) => {
      lastAdvanceFilterParams.current = params;
      dispatch({ type: "FETCH_ADVANCE_FILTERS_START" });
      try {
        const response = await getAdvanceCalendarFilters(params);
        if (response?.isSuccessful) {
          const pag = response.pagination ?? {};
          dispatch({
            type: "FETCH_ADVANCE_FILTERS_SUCCESS",
            payload: {
              advanceFilterAppointments: response.appointments ?? [],
              advanceFilterResultCount: pag.total ?? 0,
              advanceFilterPagination: {
                page: pag.page ?? 1,
                total: pag.total ?? 0,
                totalPages: pag.totalPages ?? 1,
              },
            },
          });
        } else {
          throw new Error("Failed to fetch advance filter appointments");
        }
      } catch (err) {
        console.error("Error fetching advance filter appointments:", err);
        dispatch({
          type: "FETCH_ADVANCE_FILTERS_ERROR",
          payload:
            err instanceof Error
              ? err
              : new Error("Failed to fetch advance filter appointments"),
        });
      }
    },
    [],
  );

  const refetchAdvanceFilters = useCallback(async () => {
    if (!lastAdvanceFilterParams.current) return;
    await fetchAdvanceFilters(lastAdvanceFilterParams.current);
  }, [fetchAdvanceFilters]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await getAppointmentTemplates();
      if (response?.isSuccessful) {
        const parsedTemplates = response.templates.map((template: JObj) => ({
          ...template,
          dateTimeSlots:
            typeof template.dateTimeSlots === "string"
              ? JSON.parse(template.dateTimeSlots)
              : template.dateTimeSlots,
          appointmentIds:
            typeof template.appointmentIds === "string"
              ? JSON.parse(template.appointmentIds)
              : template.appointmentIds,
          days:
            typeof template.days === "string"
              ? JSON.parse(template.days)
              : template.days,
        }));
        dispatchAction({
          type: "SET_APPOINTMENT_TEMPLATES",
          payload: { appointmentTemplates: parsedTemplates },
        });
      }
    } catch (err) {
      console.error("Error fetching appointment templates:", err);
    }
  }, [dispatchAction]);

  // Fetch templates on mount (skip if already loaded)
  useEffect(() => {
    if (!data?.appointmentTemplates?.length) {
      fetchTemplates();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <OfficeCalendarContext.Provider
      value={{
        ...state,
        patchCalendarAppointment,
        fetchCalendarData,
        refetchCalendarData,
        fetchPanelData,
        refetchPanelData,
        fetchAdvanceFilters,
        refetchAdvanceFilters,
        fetchTemplates,
        viewMode,
        setViewMode,
        isPanelOpen,
        setIsPanelOpen,
      }}
    >
      {children}
    </OfficeCalendarContext.Provider>
  );
};

export const useOfficeCalendarContext = () => {
  const context = useContext(OfficeCalendarContext);
  if (!context) {
    throw new Error(
      "useOfficeCalendarContext must be used within OfficeCalendarProvider",
    );
  }
  return context;
};
